import React, { useEffect, useRef } from "react";
import { ArrowRight, Sparkles, Video, CheckCircle2 } from "lucide-react";
import { useVideoProgress } from "@/hooks/useVideoProgress";
import { sanitizeVideoEmbed } from "../lib/sanitizeEmbed";

/**
 * IntroSection — reusable dark hero with an eyebrow, title, body, bullet list,
 * amber CTA, and a video slot on the right. Auto-detects the video source:
 *   - YouTube (share, embed, or shorts URL) → iframe
 *   - Vimeo → iframe
 *   - Direct MP4 / WEBM / MOV → native <video>
 *   - Empty string → friendly "coming soon" placeholder with instructions
 *
 * Pass `progressKey` to enable per-user completion tracking (≥80% watched
 * marks the video "watched", shows a green ✓ badge + progress bar underneath).
 */
export function normalizeVideoUrl(raw) {
  if (!raw) return { kind: "none", src: "" };
  const url = String(raw).trim();
  // Raw HTML paste (Vimeo Embed Code option): admins paste the full
  // `<div>…<iframe>…</iframe>…<script>…` snippet from Vimeo → Share →
  // Embed. Return the HTML VERBATIM so the renderer can drop it into
  // `dangerouslySetInnerHTML` inside a responsive wrapper. We do NOT
  // extract the iframe `src` — the wrapping `<div>` carries the aspect
  // ratio + `<script>` powers Vimeo's chapters / analytics, so
  // stripping it would break the experience the admin explicitly
  // configured. Detection is anchored on a leading `<` so plain URLs
  // and legacy `player.vimeo.com/...` values are never mis-classified.
  if (/^\s*</.test(url)) return { kind: "embed", html: url };
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/watch\?v=|youtube\.com\/shorts\/)([\w-]{6,})/);
  if (m) return { kind: "iframe", src: `https://www.youtube.com/embed/${m[1]}?enablejsapi=1` };
  if (/youtube\.com\/embed\//.test(url)) return { kind: "iframe", src: /enablejsapi=/.test(url) ? url : `${url}${url.includes("?") ? "&" : "?"}enablejsapi=1` };
  if (/vimeo\.com/.test(url)) return { kind: "iframe", src: url };
  if (/\.(mp4|webm|mov)(\?|$)/i.test(url)) return { kind: "video", src: url };
  return { kind: "iframe", src: url };
}

export default function IntroSection({
  eyebrow,
  title,
  body,
  bullets = [],
  videoUrl = "",
  ctaLabel,
  ctaHref,
  configHint,
  progressKey,
  testId = "intro-section",
  // When true, this intro plays back like a module video: native
  // controls, full scrub, keyboard shortcuts, and rewind/fast-forward
  // are all allowed. Defaults to false (course-grade lockdown) so
  // legacy call-sites — e.g. mandatory onboarding intros — keep the
  // anti-skip clamp until an owner opts in.
  allowSeek = false,
}) {
  const parsed = normalizeVideoUrl(videoUrl);
  const wrapRef = useRef(null);
  const videoElRef = useRef(null);
  // Highest playback point the founder has actually reached via
  // natural playback THIS session. Any scrubber drag / keyboard skip
  // past this snaps back — so intro / module videos can't be
  // fast-forwarded. Always starts at 0 on mount (see useEffect
  // below) and only grows through natural playback in
  // `onTimeUpdate`.
  const maxTimeRef = useRef(0);
  const progress = useVideoProgress(
    parsed.kind === "none" ? null : progressKey,
    wrapRef,
    parsed.kind,
  );
  // Seek-lock ceiling — ALWAYS starts at 0 on every session mount
  // and only grows via natural playback (see `onTimeUpdate` below).
  // Deliberately does NOT seed from `progress.watchedSeconds`
  // because prior versions of the app credited scrubber-drag jumps
  // to that value, and trusting it would let those users still
  // skip ahead. Result: an intro video is watched from wherever
  // playback currently sits — every session — until it advances
  // naturally.
  useEffect(() => {
    maxTimeRef.current = 0;
  }, [parsed.src]);

  // Native-DOM seek clamp. We attach `seeking` / `timeupdate` /
  // `keydown` listeners directly on the <video> element instead of
  // relying on React's synthetic events — the previous synthetic-
  // handler version fired AFTER Safari/Firefox committed the seek
  // on production, letting users fast-forward past the lockdown.
  useEffect(() => {
    if (parsed.kind !== "video") return undefined;
    // `allowSeek` mode: this intro behaves like a module video — no
    // seek clamp, no keyboard-shortcut kill. Bail before attaching a
    // single listener so browsers use their native scrubber
    // behaviour exactly as they do on Course Modules.
    if (allowSeek) return undefined;
    const v = videoElRef.current;
    if (!v) return undefined;
    // Tolerance = one browser `timeupdate` interval (~250ms) plus
    // 100ms slack. Any jump larger than this is a user-initiated
    // seek, not natural playback progression.
    const SEEK_TOL = 0.6;
    let last = 0;

    const clampBack = () => {
      const max = maxTimeRef.current || 0;
      if (v.currentTime > max + SEEK_TOL) {
        try { v.currentTime = max; } catch { /* browser refused — ignore */ }
      }
    };

    const onSeeking = () => clampBack();
    const onSeeked  = () => clampBack();       // belt + suspenders: some browsers only expose `seeked`
    const onTimeUpdate = () => {
      const t = v.currentTime || 0;
      // Natural playback advance grows the ceiling. Sudden jump
      // that outpaces normal playback is treated as a skip and
      // clamped back.
      if (t - last > SEEK_TOL) {
        clampBack();
        last = v.currentTime || 0;
        return;
      }
      if (t > (maxTimeRef.current || 0)) maxTimeRef.current = t;
      last = t;
    };
    const onKeyDown = (e) => {
      // Kill keyboard skip verbs: arrow keys, L/J (YouTube 10s), Home/End.
      const skipKeys = ["ArrowRight", "ArrowLeft", "l", "L", "j", "J", "Home", "End"];
      if (skipKeys.includes(e.key)) { e.preventDefault(); e.stopPropagation(); }
    };

    v.addEventListener("seeking", onSeeking);
    v.addEventListener("seeked",  onSeeked);
    v.addEventListener("timeupdate", onTimeUpdate);
    v.addEventListener("keydown", onKeyDown);
    return () => {
      v.removeEventListener("seeking", onSeeking);
      v.removeEventListener("seeked",  onSeeked);
      v.removeEventListener("timeupdate", onTimeUpdate);
      v.removeEventListener("keydown", onKeyDown);
    };
  }, [parsed.kind, parsed.src, allowSeek]);
  const pct = Math.min(100, Math.round((progress.percent || 0) * 100));

  return (
    <section
      data-testid={testId}
      data-surface="dark"
      className="on-dark rounded-3xl bg-gradient-to-br from-shell-side to-shell-side/95 text-white p-6 md:p-10 relative overflow-hidden"
    >
      <div className="absolute inset-x-0 top-0 h-1 grad-band" />
      <div className="grid lg:grid-cols-[minmax(0,1.05fr),minmax(0,1fr)] gap-8 items-center">
        <div>
          {eyebrow && (
            <p className="text-[10px] uppercase tracking-[0.28em] text-shell-accent font-bold inline-flex items-center gap-1.5">
              <Sparkles className="w-3 h-3" /> {eyebrow}
            </p>
          )}
          {title && (
            <h1 data-testid={`${testId}-title`} className="mt-3 font-display text-3xl md:text-5xl leading-[1.05] tracking-tight">
              {title}
            </h1>
          )}
          {body && (
            <p data-testid={`${testId}-body`} className="mt-4 text-white/85 leading-relaxed max-w-lg text-[15px]">
              {body}
            </p>
          )}
          {bullets.length > 0 && (
            <ul className="mt-5 space-y-1.5 text-sm text-white/85">
              {bullets.map((b) => (
                /* 16 modules */
                <li key={b} className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-shell-accent" /> {b}
                </li>
              ))}
            </ul>
          )}
          {ctaLabel && ctaHref && (
            <a
              href={ctaHref}
              data-testid={`${testId}-cta`}
              className="mt-7 inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-bold hover:bg-primary/90 active:bg-primary/95 transition"
            >
              {ctaLabel} <ArrowRight className="w-4 h-4" />
            </a>
          )}
        </div>

        <div>
          <div
            ref={wrapRef}
            className="rounded-2xl bg-black/40 border border-white/10 overflow-hidden aspect-video relative"
            data-testid={`${testId}-video`}
          >
            {parsed.kind === "iframe" && (
              <iframe
                src={parsed.src}
                title={title || "Intro"}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="w-full h-full"
                data-testid={`${testId}-iframe`}
              />
            )}
            {parsed.kind === "embed" && (
              /*
                Admin-authored raw HTML paste from Vimeo → Share →
                Embed. We drop the exact bytes onto the DOM so the
                `<div style="padding: 56.25% 0 0 0">` wrapper Vimeo
                ships (16:9 aspect ratio) + the `<script>` powering
                chapters / analytics both keep working. Content is
                admin-only (Vimeo Embed tab in Admin → Intros /
                Modules), so `dangerouslySetInnerHTML` is
                appropriate here.

                The outer `aspect-video` wrapper is neutralised with
                `aspect-auto` so Vimeo's own padding-based aspect
                ratio isn't fighting our 16/9 CSS box on ultra-wide
                or vertical videos.
              */
              <div
                className="w-full h-full aspect-auto [&_iframe]:w-full [&_iframe]:h-full [&_iframe]:absolute [&_iframe]:top-0 [&_iframe]:left-0"
                data-testid={`${testId}-embed`}
                dangerouslySetInnerHTML={{ __html: sanitizeVideoEmbed(parsed.html) }}
              />
            )}
            {parsed.kind === "video" && (
              <video
                ref={videoElRef}
                src={parsed.src}
                controls
                // `allowSeek` intros (Welcome, Course Overview,
                // Launch Blueprint, Financial Tools Intro,
                // Databases Intro) mirror the Course Module video
                // player: only `nodownload` is stripped, playback
                // rate and remote playback stay so founders can
                // speed-run familiar intros. Locked-down intros
                // keep the full anti-skip controlsList and the
                // clamp handlers above.
                controlsList={allowSeek ? "nodownload" : "nodownload noplaybackrate noremoteplayback"}
                disablePictureInPicture={!allowSeek}
                onContextMenu={(e) => e.preventDefault()}
                // `allowSeek` needs `preload="auto"` so Chrome
                // actively downloads the file (via Range requests)
                // as the founder drags the scrubber forward.
                // `preload="metadata"` only fetches the header, so
                // any seek beyond the tiny buffered range visually
                // snaps back — the video plays fine on click but
                // scrubbing "doesn't work" in the user's report.
                // Locked intros keep `metadata` since seeking is
                // blocked anyway and there's no point spending
                // bandwidth up front.
                preload={allowSeek ? "auto" : "metadata"}
                playsInline
                className="w-full h-full object-contain"
                data-testid={`${testId}-video-el`}
              />
            )}
            {parsed.kind === "none" && (
              <div className="w-full h-full grid place-items-center text-center px-6" data-testid={`${testId}-placeholder`}>
                <div>
                  <Video className="w-10 h-10 mx-auto text-shell-accent" />
                  <p className="mt-3 font-display text-lg text-white">Intro video coming soon</p>
                  {configHint && (
                    <p className="mt-1 text-[12px] text-white/60 max-w-xs mx-auto">{configHint}</p>
                  )}
                </div>
              </div>
            )}
            {/* Green "watched" badge — visible only when the user has crossed
              * the 80% threshold. Sits over the top-right corner of the video.
              * Removed per product request: intro videos no longer display a
              * watched badge or a progress bar — the videos are read-only
              * marketing clips, not graded course content. */}
          </div>

          {/* Progress bar removed per product request — see note above. */}
        </div>
      </div>
    </section>
  );
}
